import { CalendarDate, parseDate } from '@internationalized/date';
import { RangeValue } from '@react-types/shared/src/inputs';
import { ComponentProps, useState } from 'react';
import { useIntl } from 'react-intl';

import DateCalendar from '../../../../../components/DateCalendar/DateCalendar';
import DateRangeCalendar from '../../../../../components/DateCalendar/DateRangeCalendar';
import DatePeriod from '../../../../../components/DatePeriod/DatePeriod';
import DimmableCard from '../../../../../components/DimmableCard/DimmableCard';
import Inline from '../../../../../components/Inline/Inline';
import NearestDatePickerWithOthers from '../../../../../components/NearestDatePickerWithOthers/NearestDatePickerWithOthers';
import RadioBar, { Option } from '../../../../../components/RadioBar/RadioBar';
import Row from '../../../../../components/Row/Row';
import Stack from '../../../../../components/Stack/Stack';
import Title from '../../../../../components/Title/Title';
import guessUserTimeZone from '../../../../../helpers/date/guessUserTimeZone';
import today from '../../../../../helpers/date/today';
import usePropControlledState from '../../../../../hooks/react/usePropControlledState';
import useIsMobile from '../../../../../hooks/ui/useIsMobile';
import useTrans from '../../../../../hooks/ui/useTrans';
import DatePickerMode from '../../../../../types/DatePickerMode';
import FormDimmableCard from '../../../../components/FormDimmableCard/FormDimmableCard';
import useComposeBookingProps from '../../hooks/useComposeBookingProps';
import RequestedDates from '../../types/RequestedDates';

import useDatePickerMode from './hooks/useDatePickerMode';

const rangeSupportingModes: Array<DatePickerMode> = [
    'OnlyMultiDay',
    'PrimarilyMultiDay',
    'PrimarilySingleDay',
] as const;

type PickupDateCardProps = {
    value: RequestedDates | undefined;
    onInteractingWithDatePicker?: (interacting: boolean) => void;
    onChange: (value: RequestedDates | undefined) => void;
} & ComponentProps<typeof FormDimmableCard>;

const PickupDateCard = ({
    dimmed,
    onInteractingWithDatePicker,
    value,
    onChange,
    ...cardProps
}: PickupDateCardProps) => {
    const intl = useIntl();
    const isMobile = useIsMobile();
    const todayValue = today();
    const trans = useTrans('CustomerArea.BookingForm.PickupDateCard.');
    const {
        minimumDate,
        datePickerMode: serverSideDatePickerMode,
        errors,
    } = useComposeBookingProps();
    const [lastRequestedDates, setLastRequestedDates] = usePropControlledState<
        RequestedDates | undefined
    >(value);

    const handleChange = (newValue: RequestedDates | undefined) => {
        setLastRequestedDates(newValue);
        onChange(newValue);
    };

    const { datePickerMode, allowSwitch, changeDatePickerMode } =
        useDatePickerMode({
            serverSideDatePickerMode,
            onChange: handleChange,
            currentPickupDate: lastRequestedDates?.pickupDate,
        });

    const range = rangeSupportingModes.includes(datePickerMode);

    const overrideOptions: Array<Option<DatePickerMode>> = [
        { value: 'OnlySingleDay', label: trans('Override.SingleDay') },
        { value: 'PrimarilyMultiDay', label: trans('Override.MultiDay') },
    ];

    const datesAsRange: RangeValue<CalendarDate> | undefined =
        lastRequestedDates
            ? {
                  start: lastRequestedDates.pickupDate,
                  end: lastRequestedDates.returnDate,
              }
            : undefined;

    const [showingCalendarModal, setShowingCalendarModal] = useState(false);

    const timeZone = guessUserTimeZone();
    const minimumAsDateValue = minimumDate ? parseDate(minimumDate) : undefined;

    const openDialog = () => {
        setShowingCalendarModal(true);

        if (onInteractingWithDatePicker) {
            onInteractingWithDatePicker(true);
        }
    };

    const closeDialog = () => {
        setShowingCalendarModal(false);

        if (onInteractingWithDatePicker) {
            onInteractingWithDatePicker(false);
        }
    };

    const determineTitle = () => {
        if (range) {
            if (!lastRequestedDates) {
                return trans('DateRangeTitle');
            }

            return (
                <DatePeriod
                    start={lastRequestedDates.pickupDate}
                    end={lastRequestedDates.returnDate}
                    timeZone={timeZone}
                    hideYear={
                        todayValue.year === lastRequestedDates.pickupDate.year
                    }
                />
            );
        }

        if (!lastRequestedDates) {
            return trans('SingleDateTitle');
        }

        return intl.formatDate(lastRequestedDates.pickupDate.toDate(timeZone), {
            dateStyle: 'long',
            timeZone: timeZone,
        });
    };

    const renderDatePicker = () => {
        switch (datePickerMode) {
            case 'OnlySingleDay':
                return (
                    <NearestDatePickerWithOthers
                        value={lastRequestedDates?.pickupDate}
                        minimum={minimumAsDateValue}
                        moreLabel={trans('MoreLabel')}
                        errors={errors?.requestedPickupDate}
                        defaultStart={minimumAsDateValue}
                        onModalOpen={openDialog}
                        onModalClose={closeDialog}
                        onChange={(newValue) => {
                            handleChange(
                                newValue
                                    ? {
                                          pickupDate: newValue,
                                          returnDate: newValue,
                                      }
                                    : undefined
                            );
                        }}
                    />
                );

            case 'PrimarilySingleDay':
                return (
                    <DateCalendar
                        value={lastRequestedDates?.pickupDate}
                        minValue={minimumAsDateValue}
                        visibleMonths={isMobile ? 1 : 2}
                        minDetailLevel="day"
                        onChange={(newValue) =>
                            handleChange({
                                pickupDate: newValue,
                                returnDate: newValue,
                            })
                        }
                    />
                );

            case 'PrimarilyMultiDay':
            case 'OnlyMultiDay':
                return (
                    <DateRangeCalendar
                        value={datesAsRange}
                        minValue={minimumAsDateValue}
                        visibleMonths={isMobile ? 1 : 2}
                        minDetailLevel="day"
                        onChange={(newValue) =>
                            handleChange({
                                pickupDate: newValue.start,
                                returnDate: newValue.end,
                            })
                        }
                        singleDateButtonLabel={trans('SingleDateButtonLabel')}
                        allowSingleDayRange={datePickerMode !== 'OnlyMultiDay'}
                    />
                );
        }

        return null;
    };

    return (
        <DimmableCard {...cardProps} dimmed={dimmed || showingCalendarModal}>
            <Stack space="xlarge">
                <Inline
                    vertical="middle"
                    horizontal="justify"
                    flexChildren={[1, 1]}
                >
                    <Title variant="area" noWrapping>
                        {determineTitle()}
                    </Title>

                    {allowSwitch && (
                        <Row horizontal="right" flex>
                            <RadioBar
                                options={overrideOptions}
                                value={
                                    datePickerMode.includes('Single')
                                        ? 'OnlySingleDay'
                                        : 'PrimarilyMultiDay'
                                }
                                onChange={changeDatePickerMode}
                                withBackground
                            />
                        </Row>
                    )}
                </Inline>

                {renderDatePicker()}
            </Stack>
        </DimmableCard>
    );
};

export default PickupDateCard;
